import { type ReactNode, useEffect } from 'react';
import { useAuthStore } from '../store/auth.js';
import { fetchMe } from './api.js';

/**
 * Boot-time auth bootstrap. Runs `GET /auth/me` exactly once and pushes the
 * result into `useAuthStore`. Renders children immediately — the splash for
 * `status === 'unknown'` is intentionally minimal so the initial paint isn't
 * blocked on a network round-trip.
 *
 * No React context: Zustand is the source of truth. `useCurrentUser()` is
 * just a typed selector hook.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);

  useEffect(() => {
    let cancelled = false;
    const setUser = useAuthStore.getState().setUser;
    const setStatus = useAuthStore.getState().setStatus;

    setStatus('loading');
    fetchMe()
      .then((user) => {
        if (cancelled) return;
        setUser(user);
      })
      .catch(() => {
        if (cancelled) return;
        setUser(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (status === 'unknown') {
    return <AuthBootSplash />;
  }
  return <>{children}</>;
}

export function useCurrentUser() {
  return useAuthStore((s) => s.user);
}

function AuthBootSplash() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-paperDeep">
      <span className="stencil text-[11px] tracking-capsWide text-brass/60">
        Initialising charter…
      </span>
    </div>
  );
}
